import { useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import { CloseBold, Search16 } from '@elfsight/icons';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { ItemIcon } from '@elfsight/widget-modal';
import type { TextControlBaseInstance } from '@elfsight/widget-ui';
import { TextControlBaseContainer } from '@elfsight/widget-ui';
import { getInvertedColor } from '@elfsight/widget-utils';
import { ShortText } from '../../short-text';
import { invertedColors } from '../colors';

interface SearchInputProps {
  value: string;
  isOpen: boolean;
  onChange: (val: string) => void;
  fieldColor: string;
  fieldFillColor: string;
  fieldActiveColor: string;
  dropdownBorderRadius: number;
  searchPlaceholderText: string;
}

const COUNTRY_NAME_MAX_LENGTH = 65;

export const SearchInput = ({
  value,
  isOpen,
  onChange,
  fieldColor,
  fieldFillColor,
  fieldActiveColor,
  searchPlaceholderText,
  dropdownBorderRadius
}: SearchInputProps) => {
  const handleChange = (value: string) => {
    onChange(value);
  };

  const searchInputRef = useRef<TextControlBaseInstance>(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const timeoutId = setTimeout(() => {
      searchInputRef.current?.focusInput();
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isOpen]);

  const handleClear = () => {
    onChange('');
  };

  return (
    <Container
      _backgroundColor={getInvertedColor(fieldColor, invertedColors.background)}
      _bottomBorderColor={getInvertedColor(fieldColor, invertedColors.border)}
    >
      <ShortText
        ref={searchInputRef}
        variants={{
          focused: { borderColor: fieldActiveColor },
          initial: { borderColor: getColorWithAlpha(fieldColor, 0.2) }
        }}
        placeholder={searchPlaceholderText}
        value={value}
        maxLength={COUNTRY_NAME_MAX_LENGTH}
        fieldFillColor={fieldFillColor}
        placeholderColor={getColorWithAlpha(fieldColor, 0.5)}
        fieldColor={fieldColor}
        fieldBorderRadius={Math.max(4, dropdownBorderRadius - 16)}
        padding={[8, 12]}
        leftSlot={
          <SearchIcon _color={fieldColor} component={Search16} size={16} />
        }
        rightSlot={
          value && (
            <ClearButton fieldColor={fieldColor} onClick={handleClear}>
              <ItemIcon component={CloseBold} size={8} />
            </ClearButton>
          )
        }
        onChange={handleChange}
        onBlur={(e) => e.stopPropagation()}
      ></ShortText>
    </Container>
  );
};

const ClearButton = styled.div<{ fieldColor: string }>`
  width: 16px;
  height: 16px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  padding: 0;
  background-color: ${({ fieldColor }) => getColorWithAlpha(fieldColor, 0.1)};

  & > div {
    padding: 0 !important;
  }

  & svg {
    width: 8px;
    height: 8px;
    fill: ${({ fieldColor }) => getColorWithAlpha(fieldColor, 0.7)};
    transition: fill 120ms ease-in-out;
  }

  &:hover svg {
    fill: ${({ fieldColor }) => getColorWithAlpha(fieldColor, 1)};
  }
`;

const SearchIcon = styled(ItemIcon)<{ _color: string }>`
  fill: ${({ _color }) => getColorWithAlpha(_color, 0.3)};
`;

const Container = styled.div<{
  _backgroundColor: string;
  _bottomBorderColor: string;
}>`
  padding: 16px;
  border-bottom: 1px solid ${({ _bottomBorderColor }) => _bottomBorderColor};
  background-color: ${({ _backgroundColor }) => _backgroundColor};
  z-index: 1;
  margin-bottom: 4px;

  & div${TextControlBaseContainer} {
    padding: 8px 12px !important;
  }
`;
